import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, PanelLeftClose, PanelLeft } from 'lucide-react';
import {
  NAVIGATION_TREE,
  isNavHrefActive,
  type NavEntry,
  type NavLeaf,
  type PermissionAction,
} from '../config/navigation';

const OPEN_GROUPS_KEY = 'sidebarOpenGroups';
export const SIDEBAR_ICON_COLLAPSE_KEY = 'sidebarIconCollapsed';

type CanFn = (module: string, action?: PermissionAction) => boolean;

function leafVisible(leaf: NavLeaf, can: CanFn): boolean {
  return can(leaf.module, leaf.action ?? 'read');
}

function entryVisible(entry: NavEntry, can: CanFn): boolean {
  if (entry.type === 'separator') return true;
  if (entry.type === 'link') return can(entry.module, entry.action ?? 'read');
  return entry.children.some((c) => leafVisible(c, can));
}

function groupContainsActive(entry: Extract<NavEntry, { type: 'group' }>, pathname: string): boolean {
  return entry.children.some((c) => isNavHrefActive(pathname, c.href));
}

export function AppSidebar({
  can,
  loading,
  mobileOpen,
  onMobileClose,
  forceHidden,
  iconCollapsed,
  onIconCollapsedChange,
}: {
  can: CanFn;
  loading: boolean;
  mobileOpen: boolean;
  onMobileClose: () => void;
  forceHidden?: boolean;
  iconCollapsed: boolean;
  onIconCollapsedChange: (collapsed: boolean) => void;
}) {
  const location = useLocation();
  const pathname = location.pathname;

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(OPEN_GROUPS_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      // Operativo abierto por defecto la primera vez
      if (parsed.operations === undefined) parsed.operations = true;
      return parsed;
    } catch {
      return { operations: true };
    }
  });

  const visibleTree = useMemo(
    () => NAVIGATION_TREE.filter((e) => entryVisible(e, can)),
    [can],
  );

  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const entry of NAVIGATION_TREE) {
        if (entry.type === 'group' && groupContainsActive(entry, pathname) && !next[entry.id]) {
          next[entry.id] = true;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [pathname]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
    } catch {
      /* ignore */
    }
  }, [openGroups]);

  const toggleGroup = (id: string) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const narrow = iconCollapsed && !forceHidden;

  const shellClass = [
    'fixed inset-y-0 left-0 z-50 bg-white shadow-lg border-r border-gray-200',
    'transform transition-all duration-300 ease-in-out flex flex-col h-screen',
    narrow ? 'w-16' : 'w-64',
    forceHidden
      ? '-translate-x-full'
      : mobileOpen
        ? 'translate-x-0'
        : '-translate-x-full lg:translate-x-0',
  ].join(' ');

  return (
    <aside className={shellClass} aria-label="Navegación principal">
      <div className="flex shrink-0 items-center justify-between h-16 px-3 border-b border-gray-200 gap-2">
        {!narrow && (
          <h1 className="text-base font-semibold text-gray-900 truncate pl-1">
            Admin <span style={{ color: '#3C1D8C' }}>creditnow</span>
          </h1>
        )}
        <div className={`flex items-center gap-1 ${narrow ? 'mx-auto' : ''}`}>
          <button
            type="button"
            className="hidden lg:inline-flex p-1.5 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            title={narrow ? 'Expandir menú' : 'Colapsar menú'}
            onClick={() => onIconCollapsedChange(!iconCollapsed)}
          >
            {narrow ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={onMobileClose}
            className="lg:hidden p-1.5 rounded text-gray-500 hover:bg-gray-100"
            title="Cerrar menú"
          >
            ×
          </button>
        </div>
      </div>

      <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain py-2">
        {loading ? (
          <p className="px-3 py-2 text-xs text-gray-400">Cargando menú…</p>
        ) : visibleTree.length === 0 ? (
          <p className="px-3 py-2 text-xs text-gray-500">Sin módulos habilitados</p>
        ) : (
          visibleTree.map((entry) => {
            if (entry.type === 'separator') {
              return <div key={entry.id} className={`my-2 ${narrow ? 'mx-2' : 'mx-3'} border-t border-gray-200`} />;
            }

            if (entry.type === 'link') {
              const Icon = entry.icon;
              const active = isNavHrefActive(pathname, entry.href);
              return (
                <Link
                  key={entry.id}
                  to={entry.href}
                  title={entry.label}
                  onClick={onMobileClose}
                  className={[
                    'flex items-center gap-3 mx-2 my-0.5 rounded-md text-sm font-medium transition-colors',
                    narrow ? 'justify-center px-2 py-2.5' : 'px-3 py-2',
                    active
                      ? 'bg-slate-100 text-slate-900 ring-1 ring-inset ring-slate-200'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900',
                  ].join(' ')}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  {!narrow && <span className="truncate">{entry.label}</span>}
                </Link>
              );
            }

            const Icon = entry.icon;
            const children = entry.children.filter((c) => leafVisible(c, can));
            if (children.length === 0) return null;

            const open = Boolean(openGroups[entry.id]) || groupContainsActive(entry, pathname);
            const groupActive = groupContainsActive(entry, pathname);

            if (narrow) {
              return (
                <div key={entry.id} className="mx-2 my-0.5">
                  <button
                    type="button"
                    title={entry.label}
                    onClick={() => {
                      onIconCollapsedChange(false);
                      setOpenGroups((prev) => ({ ...prev, [entry.id]: true }));
                    }}
                    className={[
                      'flex w-full items-center justify-center rounded-md px-2 py-2.5 text-sm',
                      groupActive ? 'bg-slate-100 text-slate-900' : 'text-gray-700 hover:bg-gray-50',
                    ].join(' ')}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </button>
                </div>
              );
            }

            let lastSection: string | undefined;

            return (
              <div key={entry.id} className="mx-2 my-0.5">
                <button
                  type="button"
                  onClick={() => toggleGroup(entry.id)}
                  className={[
                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    groupActive ? 'text-slate-900 bg-slate-50' : 'text-gray-700 hover:bg-gray-50',
                  ].join(' ')}
                  aria-expanded={open}
                >
                  <Icon className="h-4 w-4 shrink-0 opacity-80" aria-hidden />
                  <span className="flex-1 text-left truncate">{entry.label}</span>
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {open && (
                  <div className="mt-0.5 mb-1 ml-3 border-l border-gray-200 pl-2 space-y-0.5">
                    {children.map((child) => {
                      const showSection = Boolean(child.section && child.section !== lastSection);
                      if (child.section) lastSection = child.section;
                      const active = isNavHrefActive(pathname, child.href);
                      return (
                        <div key={child.id}>
                          {showSection && (
                            <p className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              {child.section}
                            </p>
                          )}
                          <Link
                            to={child.href}
                            onClick={onMobileClose}
                            className={[
                              'block rounded-md px-2 py-1.5 text-sm transition-colors',
                              active
                                ? 'bg-slate-100 font-medium text-slate-900'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            ].join(' ')}
                          >
                            {child.label}
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        )}
      </nav>
    </aside>
  );
}
